import { useEffect, useState } from "react";
import { createTipPage, listTipPages, tipPageLinks, type TipPage, type TipPageLinks } from "../api";
import type { T } from "../i18n";

export function TipPages({ t }: { t: T }) {
  const [items, setItems] = useState<TipPage[]>([]);
  const [recipientID, setRecipientID] = useState("");
  // MXN and round peso amounts: every venue here is Mexican, and a form that
  // opens on dollars is one a manager has to correct before every page.
  const [currency, setCurrency] = useState("MXN");
  const [presets, setPresets] = useState("2000,3000,5000");
  const [es, setEs] = useState("");
  const [en, setEn] = useState("");
  const [ru, setRu] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [links, setLinks] = useState<TipPageLinks | null>(null);

  const load = () => listTipPages().then((r) => setItems(r.tip_pages)).catch(() => setError(t("common.error")));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function add() {
    setError(null);
    try {
      const preset_amounts = presets
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !Number.isNaN(n));
      // Keyed by locale; the guest page asks for es-MX first and falls back
      // through plain `es`. Without this field a Mexican venue could only
      // write a greeting in languages its guests do not read.
      const message_i18n: Record<string, string> = {};
      if (es) message_i18n["es-MX"] = es;
      if (en) message_i18n.en = en;
      if (ru) message_i18n.ru = ru;
      await createTipPage({ recipient_id: recipientID, currency, preset_amounts, message_i18n });
      setRecipientID("");
      setEs("");
      setEn("");
      setRu("");
      await load();
    } catch {
      setError(t("common.error"));
    }
  }

  return (
    <div>
      <div className="form-grid">
        <input placeholder={t("tippages.recipient")} value={recipientID} onChange={(e) => setRecipientID(e.target.value)} />
        <input placeholder={t("tippages.currency")} value={currency} onChange={(e) => setCurrency(e.target.value)} />
        <input placeholder={t("tippages.presets")} value={presets} onChange={(e) => setPresets(e.target.value)} />
        <input placeholder={t("tippages.greeting_es")} value={es} onChange={(e) => setEs(e.target.value)} />
        <input placeholder={t("tippages.greeting_en")} value={en} onChange={(e) => setEn(e.target.value)} />
        <input placeholder={t("tippages.greeting_ru")} value={ru} onChange={(e) => setRu(e.target.value)} />
        <button onClick={add} disabled={!recipientID || currency.length !== 3}>
          {t("tippages.add")}
        </button>
      </div>
      {error && <p className="error">{error}</p>}

      <table>
        <thead>
          <tr>
            <th>{t("tippages.slug")}</th>
            <th>{t("tippages.currency")}</th>
            <th>{t("tippages.presets_short")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id}>
              <td className="mono">{p.slug}</td>
              <td>{p.currency}</td>
              <td>{p.preset_amounts.join(", ")}</td>
              <td>
                <button className="link" onClick={() => tipPageLinks(p.id).then(setLinks).catch(() => {})}>
                  {t("tippages.links")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {links && (
        <div className="links-panel">
          <button className="close" onClick={() => setLinks(null)}>
            ×
          </button>
          <p className="mono">{links.tip_url}</p>
          <img src={links.qr_data_url} alt="QR" width={180} height={180} />
          {links.telegram_deep_link && <p className="mono">{links.telegram_deep_link}</p>}
          {links.mini_app_link && <p className="mono">{links.mini_app_link}</p>}
        </div>
      )}
    </div>
  );
}
